/**
 * Single source of truth for the Comms Platform design system: the Bhanzu
 * brand system, in a light and a dark theme.
 *
 * Every colour, surface, shadow, radius, font and icon size lives here. Pages
 * must not write a colour literal, a raw font size or a bare-number radius:
 * `theme/designSystem.test.js` fails on any, and `theme/contrast.test.js`
 * measures every pair below in both themes.
 *
 * Sources: "Bhanzu_Brand guidelines document_Final.pdf" (identity, colour,
 * Poppins) and "Bhanzu_Updated_Stylesheet_18012022.pdf" (tints, system
 * colours, type scale, frames, buttons), as written up in the wiki's
 * docs/frontend/bhanzu-brand-system.md. The Labs console
 * (applause apps/ab-testing-app/src/theme) is the reference implementation;
 * the light theme takes its values. Anything not quoted from the PDFs is
 * marked "Derived" with the reason it had to be.
 *
 * How the two themes work: a token whose value differs by theme is a CSS
 * custom property reference (`var(--cv-text-primary)`), and the active theme
 * writes the properties on `:root` (`themeVariableStyles`, set by
 * ThemeModeProvider from the `data-theme` attribute). A module-level style
 * object built from tokens therefore never goes stale: its string stays the
 * same and only the property's value changes. `resolveColor(token, mode)`
 * turns a token into the concrete colour of a theme, for MUI's palette (which
 * computes from concrete colours) and for the contrast tests. For a
 * translucent version of a token use `tint(token, amount)`, never MUI's
 * `alpha()` or a hex suffix, which both need a concrete colour.
 */

// ── Theme machinery ─────────────────────────────────────────────

export const THEME_MODES = ['light', 'dark']

const VAR_PREFIX = '--cv-'
const VAR_REF = /var\(--cv-([a-z0-9-]+)\)/g

/** name -> { light, dark }: every themed token's concrete values. */
const REGISTRY = {}

const HEX = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i
const RGB =
    /^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*(?:,\s*([\d.]+)\s*)?\)$/i
const COLOR_MIX = /color-mix\(in srgb,\s*([^%]+?)\s+([\d.]+)%,\s*transparent\)/g

/** [r, g, b, a] of a concrete hex or rgb(a) colour, or null. */
export const parseColor = (color) => {
    const value = String(color).trim()
    const hex = value.match(HEX)
    if (hex) {
        const digits =
            hex[1].length === 3
                ? hex[1]
                      .split('')
                      .map((c) => c + c)
                      .join('')
                : hex[1]
        return [0, 2, 4]
            .map((i) => parseInt(digits.slice(i, i + 2), 16))
            .concat(1)
    }
    const rgb = value.match(RGB)
    if (rgb) {
        return [
            Number(rgb[1]),
            Number(rgb[2]),
            Number(rgb[3]),
            rgb[4] === undefined ? 1 : Number(rgb[4]),
        ]
    }
    return null
}

const round = (n) => Math.round(n * 1000) / 1000

/** rgba() of a concrete colour at `amount` of its own opacity. */
const fade = (color, amount) => {
    const [r, g, b, a] = parseColor(color)
    return `rgba(${r},${g},${b},${round(a * amount)})`
}

/**
 * The concrete value of a token (or of any CSS value holding tokens) in one
 * theme: every `var(--cv-*)` is replaced by that theme's value and every
 * `tint()` (a `color-mix` with transparent) by its rgba. Values without
 * tokens come back unchanged.
 *
 * @param {string} value a token, or a CSS value such as a border or shadow
 * @param {'light'|'dark'} mode
 * @returns {string}
 */
export const resolveColor = (value, mode) => {
    if (typeof value !== 'string') return value
    let out = value
    // A registered value is already concrete, so one pass resolves it.
    out = out.replace(VAR_REF, (whole, name) => {
        const entry = REGISTRY[name]
        if (!entry) throw new Error(`Unknown theme token ${whole}`)
        return entry[mode]
    })
    out = out.replace(COLOR_MIX, (whole, color, pct) => {
        const parsed = parseColor(color)
        return parsed ? fade(color, Number(pct) / 100) : whole
    })
    return out
}

/**
 * A token, or a constant colour, at `amount` (0 to 1) of its opacity: the
 * themed replacement for MUI's `alpha()` and for `${color}1F`. A concrete
 * colour comes back as rgba; a themed one as a CSS `color-mix()`, which the
 * browser resolves against the active theme.
 */
export const tint = (color, amount) => {
    if (parseColor(color)) return fade(color, amount)
    return `color-mix(in srgb, ${color} ${round(amount * 100)}%, transparent)`
}

/**
 * Register a token whose value differs by theme and return its CSS
 * reference. Values may use tokens registered before them.
 */
const themed = (name, light, dark) => {
    if (REGISTRY[name]) throw new Error(`Theme token ${name} defined twice`)
    REGISTRY[name] = {
        light: resolveColor(light, 'light'),
        dark: resolveColor(dark, 'dark'),
    }
    return `var(${VAR_PREFIX}${name})`
}

/** Every theme token as CSS custom properties, for one theme. */
export const cssVariables = (mode) =>
    Object.fromEntries(
        Object.entries(REGISTRY).map(([name, values]) => [
            `${VAR_PREFIX}${name}`,
            values[mode],
        ])
    )

// ── Bhanzu brand (the same in both themes) ──────────────────────

export const BRAND = {
    orange: '#F15D22',
    // Derived: orange darkened 12%, kept for the contrast test's "known to
    // fail" case (Deep Blue on it is 3.6:1). Not used as a ground.
    orangeHover: '#D4521E',
    // Brand orange tint 2 (stylesheet p.02). Hover for contained primary
    // buttons: lighter, so Deep Blue type keeps AA (6.0:1).
    orangeTint: '#FE804E',
    // Derived (Labs ORANGE_DEEP): one step darker than Bhanzu Orange. Full
    // orange is 2.91:1 on the light sunken well and 2.79:1 on its own 15%
    // tint, under the 3:1 a graphical mark needs; this is 3.81 and 3.65.
    orangeDeep: '#D24E17',
    deepBlue: '#212638',
    almostWhite: '#F7F7F7',
    yellow: '#FAB82B',
    funBlue: '#5E91FF',
    justGrey: '#45474A',
}

/** The stylesheet's three-step tints (p.02): step 1 is full strength. */
export const TINT = {
    orange: [BRAND.orange, '#FE804E', '#FC9E77'],
    deepBlue: [BRAND.deepBlue, '#393E51', '#545A71'],
    neutral: [BRAND.almostWhite, '#C8C8C8', '#9B9A9A'],
    yellow: [BRAND.yellow, '#FEC953', '#FED67C'],
    funBlue: [BRAND.funBlue, '#85ACFD', '#B5CBFE'],
}

// ── System colours (stylesheet p.04) ────────────────────────────
// SURFACE hues: grounds, bars, dots and icons. As text they fail on light
// grounds (#31C853 is 2.2:1 on white); text takes STATUS.<tone>.fg.
export const SYSTEM = {
    error: '#FF2432',
    // Derived: darkened alert red for a destructive button's ground: white on
    // #FF2432 is 3.8:1, on errorDeep 5.7:1. Hover goes darker still.
    errorDeep: '#CC1122',
    errorDeepHover: '#A80E1C',
    warning: '#FAA728',
    success: '#31C853',
    // Derived: success lightened for a contained success button's hover, so
    // its Deep Blue type stays AA (8.1:1).
    successHover: '#5BD675',
    // The stylesheet's optional "Pop-up" blue.
    popup: '#2AB5F9',
}

// ── Text on brand grounds ───────────────────────────────────────
// White on Bhanzu Orange is 3.3:1 and fails AA, though the brand's font
// colour page suggests it; Deep Blue is 4.5:1 with only brand colours.
export const ON_ORANGE = BRAND.deepBlue
// White on #31C853 is 2.2:1; Deep Blue is 6.8:1.
export const ON_SUCCESS = BRAND.deepBlue
// White: the label on a DARKENED ground (the destructive red, the light
// theme's text-safe status hues), where it is 5-6:1.
export const ON_STRONG = '#FFFFFF'

// ── Surfaces ────────────────────────────────────────────────────
// Light: the brand's Almost White page and white Frames (Labs' values).
// Dark: derived from Deep Blue, so the dark theme is the brand's own dark
// rather than a borrowed near-black: the card is Deep Blue itself, the page
// one step darker, raised surfaces (header, menus, tooltips) one lighter.
export const SURFACE = {
    page: themed('surface-page', BRAND.almostWhite, '#161A27'),
    card: themed('surface-card', '#FFFFFF', BRAND.deepBlue),
    cardRaised: themed('surface-raised', '#FFFFFF', '#2B3045'),
    // Derived (Labs SUNKEN): wells, progress tracks, table header rows. In
    // the dark theme a well sits below the card, between it and the page.
    sunken: themed('surface-sunken', '#EFF0F3', '#1B1F2E'),
    // Hairline border and divider (decorative, no contrast floor).
    border: themed('surface-border', '#E3E5EA', 'rgba(247,247,247,0.10)'),
    // Emphasis border: inputs, outlined buttons, a hovered card.
    borderHover: themed(
        'surface-border-strong',
        '#CBCFD8',
        'rgba(247,247,247,0.26)'
    ),
    inputBg: themed('surface-input', '#FFFFFF', 'rgba(247,247,247,0.05)'),
    // Derived: a translucent lift that works on any ground: a hovered row,
    // menu item or list entry.
    hover: themed(
        'surface-hover',
        'rgba(33,38,56,0.05)',
        'rgba(247,247,247,0.06)'
    ),
    // Derived: the faintest ground, a panel inside a card (a code block, a
    // table header, a nested section). Text on it is in the contrast test.
    subtle: themed(
        'surface-subtle',
        'rgba(33,38,56,0.03)',
        'rgba(247,247,247,0.03)'
    ),
    // Derived: the faint lift of a hovered card.
    cardHover: themed(
        'surface-card-hover',
        'rgba(33,38,56,0.02)',
        'rgba(247,247,247,0.02)'
    ),
    // Derived: the scrim behind a modal or a drawer.
    backdrop: themed(
        'surface-backdrop',
        'rgba(33,38,56,0.45)',
        'rgba(0,0,0,0.6)'
    ),
    // An idle Stepper step's circle: the ground for its number
    // (STEP.idleNumber).
    stepIdle: themed('surface-step-idle', '#E3E5EA', 'rgba(247,247,247,0.15)'),
    // Derived: the white an email client paints behind a message, under a
    // preview of a template's stored HTML (which assumes it). Both themes.
    emailCanvas: '#FFFFFF',
}

// ── Text ────────────────────────────────────────────────────────
// Every tier that carries words clears AA (4.5:1) on the page, card and
// raised grounds, and the quietest (faint) keeps a 6:1 margin so 11-12px
// captions stay legible. Light tiers are Deep Blue and its tints carried
// toward Almost White; dark tiers are Almost White at falling opacity.
export const TEXT = {
    primary: themed('text-primary', BRAND.deepBlue, BRAND.almostWhite),
    secondary: themed('text-secondary', '#393E51', 'rgba(247,247,247,0.85)'),
    muted: themed('text-muted', '#454B60', 'rgba(247,247,247,0.74)'),
    faint: themed('text-faint', '#545A71', 'rgba(247,247,247,0.66)'),
    // Disabled controls only (WCAG exempts them), still above 3:1 so the
    // label can be read; never for text that is merely secondary.
    disabled: themed('text-disabled', '#7C8296', 'rgba(247,247,247,0.42)'),
    // Illustrations that carry no information (a large empty-state icon
    // beside its message). Never for text.
    decorative: themed(
        'text-decorative',
        'rgba(33,38,56,0.15)',
        'rgba(247,247,247,0.15)'
    ),
}

// ── Accent: Bhanzu Orange ───────────────────────────────────────
// Orange is an accent, never a page colour. Full orange draws graphical marks
// (icons, bars, focus rings, selected borders: 3:1 on the light page and card,
// more on dark). Orange WORDS take `ACCENT.text`: full orange is 3.1-3.3:1 on
// light grounds and fails as text.
export const ACCENT = {
    bg04: tint(BRAND.orange, 0.04),
    bg08: tint(BRAND.orange, 0.08),
    bg12: tint(BRAND.orange, 0.12),
    bg15: tint(BRAND.orange, 0.15),
    bg22: tint(BRAND.orange, 0.22),
    border30: tint(BRAND.orange, 0.3),
    border55: tint(BRAND.orange, 0.55),
    // Derived: Bhanzu Orange darkened for text on light grounds (6.5:1 on
    // white, 5.2 on its own tint), the way the status colours are darkened
    // for text. Dark theme: orange tint 3 (6.4:1 on raised, 5.4 on the
    // orange tint there; tint 2 was 4.4).
    text: themed('accent-text', '#A23D14', TINT.orange[2]),
    // A graphical orange mark on a well or an orange tint, where full orange
    // is under 3:1 in the light theme (BRAND.orangeDeep).
    strong: themed('accent-strong', BRAND.orangeDeep, BRAND.orange),
}

// ── Links ───────────────────────────────────────────────────────
// Derived: Fun Blue darkened for text on light grounds (6.2:1 on white);
// Fun Blue tint 2 on dark (5.8:1 on raised; full Fun Blue is 4.3).
export const LINK = themed('link', '#2F5BC2', TINT.funBlue[1])

// ── Status: success, warning, error, info, neutral ──────────────
// Text-safe foregrounds on their own tint, for chips, banners, inline
// results and AI grades. Light foregrounds are the system hues darkened for
// text (Labs' STATUS values); dark ones are the system hues themselves or a
// lighter tint where the hue is too dark on Deep Blue.
const status = (name, light, dark) => ({
    fg: themed(`status-${name}-fg`, light.fg, dark.fg),
    bg: themed(`status-${name}-bg`, light.bg, dark.bg),
    border: themed(
        `status-${name}-border`,
        tint(light.fg, 0.3),
        tint(dark.fg, 0.35)
    ),
})

export const STATUS = {
    success: status(
        'success',
        { fg: '#1E7D3C', bg: '#E4F7EA' },
        { fg: SYSTEM.success, bg: tint(SYSTEM.success, 0.14) }
    ),
    warning: status(
        'warning',
        { fg: '#8A5D00', bg: '#FDF1DA' },
        { fg: SYSTEM.warning, bg: tint(SYSTEM.warning, 0.14) }
    ),
    error: status(
        'error',
        { fg: SYSTEM.errorDeep, bg: '#FDECEE' },
        // Derived: a light red (5.2:1 on a hovered raised menu row; the
        // usual #F87171 was 3.9 there).
        { fg: '#FF9494', bg: tint('#FF9494', 0.14) }
    ),
    info: status(
        'info',
        { fg: '#2C4C8F', bg: '#E7EDFB' },
        { fg: TINT.funBlue[1], bg: tint(BRAND.funBlue, 0.14) }
    ),
    neutral: status(
        'neutral',
        { fg: '#454B60', bg: '#EFF0F3' },
        { fg: 'rgba(247,247,247,0.74)', bg: 'rgba(247,247,247,0.06)' }
    ),
}

// Error text on a card or page (field errors, failed results). Kept under
// its old name: every error message in the app uses it.
SYSTEM.errorSoft = STATUS.error.fg

// ── Categorical colours ─────────────────────────────────────────
// Identity colours that only tell things apart (an AI tool, a segment, a
// chart series), never good or bad. Each is text-safe on the card and on its
// own 12-15% tint in both themes (contrast test). Light: the brand hues and
// derived companions darkened for text; dark: the brand hues and tints.
export const CATEGORY = {
    orange: ACCENT.text,
    blue: themed('category-blue', '#2F5BC2', TINT.funBlue[1]),
    green: themed('category-green', '#0F6E36', SYSTEM.successHover),
    amber: themed('category-amber', '#8A5D00', BRAND.yellow),
    violet: themed('category-violet', '#6D45C9', '#B9A3FB'),
    pink: themed('category-pink', '#B42D6E', '#F78BC4'),
    cyan: themed('category-cyan', '#0B6178', SYSTEM.popup),
    lime: themed('category-lime', '#3F6A0A', '#A3E635'),
}

// ── Channel identity ────────────────────────────────────────────
// A channel's colour is text on its own 12% tint (ChannelTag, the template
// cards), so each is text-safe there in both themes.
export const CHANNEL_COLORS = {
    EMAIL: CATEGORY.blue,
    WHATSAPP: themed('channel-whatsapp', '#0F6E36', '#25D366'),
    SMS: CATEGORY.amber,
    VOICE: CATEGORY.violet,
    PUSH_NOTIFICATION: CATEGORY.pink,
    SLACK: themed('channel-slack', '#1A5F96', '#7DB8E8'),
}

// Third-party marks shown as themselves (both themes).
export const THIRD_PARTY = {
    // Salesforce's brand blue, for its cloud mark on the CRM pages.
    salesforce: '#00A1E0',
}

// ── Shape ───────────────────────────────────────────────────────
// PX, deliberately: MUI multiplies a bare `sx={{ borderRadius: n }}` by
// `theme.shape.borderRadius`, so a bare number is a scaling trap. The
// stylesheet's Frames are R24 and buttons are pills; cards use 20, as Labs
// does (24 is loud at console density).
export const RADIUS = {
    // Derived: tiny inline marks (a variable chip inside a text line, a kbd).
    xs: 4,
    // Chips, inputs, small controls, menus.
    sm: 8,
    // Nested tiles and panels inside a card.
    md: 12,
    // Cards, dialogs, popovers.
    lg: 20,
    // Buttons and status pills.
    pill: 999,
}

// The stylesheet's Frames shadow (0 3px 20px rgba(0,0,0,.15)), toned down so
// it sits under a hairline (Labs); the dark theme needs a deeper one to show.
export const SHADOW = {
    card: themed(
        'shadow-card',
        '0 3px 20px rgba(33,38,56,0.06)',
        '0 3px 20px rgba(0,0,0,0.25)'
    ),
    cardHover: themed(
        'shadow-card-hover',
        '0 3px 45px rgba(33,38,56,0.10)',
        '0 6px 30px rgba(0,0,0,0.38)'
    ),
    popover: themed(
        'shadow-popover',
        '0 8px 30px rgba(33,38,56,0.14)',
        '0 8px 32px rgba(0,0,0,0.5)'
    ),
}

// ── Type ────────────────────────────────────────────────────────
// Poppins for headings only; the stylesheet keeps it out of body copy "to
// increase the loading speed by using web supported type families", so body,
// captions, buttons and overlines are Roboto. The sizes live in the theme's
// variants (theme.js); use `variant="caption"` on Typography and
// `typography: 'caption'` in any other sx, never a raw font size.
export const FONT = {
    heading: "'Poppins', system-ui, -apple-system, 'Segoe UI', sans-serif",
    body: "'Roboto', system-ui, -apple-system, 'Segoe UI', Helvetica, Arial, sans-serif",
    // Derived: the brand names no monospace; ids, keys, variables and HTML
    // use the system's.
    mono: "ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace",
}

// Icon sizes (SVG icons are sized with fontSize; they are not type).
export const ICON = {
    xs: 12,
    sm: 14,
    md: 16,
    lg: 20,
    xl: 24,
    // An empty state's or a hero's illustration.
    hero: 40,
}

// ── Controls ────────────────────────────────────────────────────
// The switch (components/shared/PillToggle) and the Stepper's numbers.
export const CONTROL = {
    // Off track: the white knob on it is 3.8:1 (light) so the switch reads.
    trackOff: themed('control-track-off', '#7C8296', 'rgba(247,247,247,0.3)'),
    trackOn: BRAND.orange,
    knob: '#FFFFFF',
    focusRing: ACCENT.border55,
}

export const STEP = {
    idleNumber: themed('step-idle-number', BRAND.deepBlue, BRAND.almostWhite),
    // Active (orange) and completed (yellow) circles carry Deep Blue numbers:
    // white on orange is 3.3:1 and on yellow 1.8:1.
    activeNumber: ON_ORANGE,
    completedNumber: BRAND.deepBlue,
}

// ── Read progress bar (components/ReadProgress) ─────────────────
// The 2px bar under the header while a page reads data: a graphical object,
// so 3:1 on the header, the sidebar, the page and its own faint orange track.
export const READ_PROGRESS = {
    bar: ACCENT.strong,
    track: ACCENT.bg15,
}

// ── List pagination (components/shared/ListPagination) ──────────
export const PAGINATION = {
    text: TEXT.secondary,
    selectedText: TEXT.primary,
    selectedBg: ACCENT.bg15,
    selectedBorder: ACCENT.strong,
}

// ── Markdown (components/shared/Markdown) ───────────────────────
// The Guide page and BhanzuBot's answers.
export const MARKDOWN = {
    text: TEXT.secondary,
    heading: TEXT.primary,
    link: LINK,
    code: TEXT.primary,
    codeBg: SURFACE.subtle,
    rule: SURFACE.border,
}

// ── BhanzuBot (components/BhanzuBot) ────────────────────────────
// The launcher is the Bhanzu crescent in orange on Deep Blue (the app icon's
// colours) in both themes; the panel is the raised surface.
export const BOT = {
    launcherBg: BRAND.deepBlue,
    launcherFg: BRAND.orange,
    launcherBorder: SURFACE.borderHover,
    launcherShadow: SHADOW.popover,
    focusRing: BRAND.orange,
    panelBg: SURFACE.cardRaised,
    panelBorder: SURFACE.border,
    panelShadow: SHADOW.popover,
    userBubble: ACCENT.bg15,
    userText: TEXT.primary,
    botBubble: SURFACE.subtle,
    botText: MARKDOWN.text,
    meta: TEXT.muted,
    errorText: STATUS.error.fg,
    chipBg: ACCENT.bg15,
    chipText: TEXT.primary,
}

// ── Settings (pages/Settings) ───────────────────────────────────
// The cards are Frames (the card ground) over the page and its faint orange
// glow; the glow is half as strong on the light page, where 8% orange read as
// a peach wash across the cards' margins.
export const SETTINGS = {
    cardGround: SURFACE.card,
    glow: themed(
        'settings-glow',
        tint(BRAND.orange, 0.04),
        tint(BRAND.orange, 0.08)
    ),
    providerNote: TEXT.secondary,
    switchSpinner: ACCENT.strong,
    modelLabel: TEXT.muted,
    model: TEXT.primary,
}

// ── Template Writer results (pages/AI/TemplateWriter) ───────────
// The cards are the brand's Frames (cardSx: white on light, Deep Blue on
// dark) over the page and its faint orange glow; a translucent ground read as
// part of the Almost White page in the light theme.
export const TEMPLATE_WRITER = {
    cardGround: SURFACE.card,
    glow: tint(BRAND.orange, 0.1),
    toggleText: TEXT.secondary,
    toggleSelectedText: TEXT.primary,
    toggleSelectedBg: ACCENT.bg15,
    toggleBorder: SURFACE.border,
}

// ── WhatsApp preview ────────────────────────────────────────────
// Derived, not brand values: WhatsApp's own chat colours, light or dark like
// the phone, so the Create Template preview looks like the message.
export const WHATSAPP_PREVIEW = {
    canvas: themed('wa-canvas', '#EFEAE2', '#111b21'),
    bubble: themed('wa-bubble', '#FFFFFF', '#202c33'),
    text: themed('wa-text', '#111B21', '#e9edef'),
    // Footer and timestamp text (WhatsApp's secondary grey on the bubble).
    meta: themed('wa-meta', '#667781', 'rgba(233,237,239,0.6)'),
    placeholder: themed('wa-placeholder', '#667781', 'rgba(233,237,239,0.5)'),
    // Read ticks and quick-reply / call-to-action button labels.
    // Light: WhatsApp's link blue darkened to 5.3:1 on the document tile.
    link: themed('wa-link', '#016A99', '#53bdeb'),
    divider: themed(
        'wa-divider',
        'rgba(17,27,33,0.12)',
        'rgba(233,237,239,0.12)'
    ),
    // The ground of a header document tile inside the bubble.
    tile: themed('wa-tile', 'rgba(17,27,33,0.06)', 'rgba(233,237,239,0.08)'),
    bubbleShadow: themed(
        'wa-bubble-shadow',
        '0 1px 0.5px rgba(11,20,26,0.13)',
        '0 1px 3px rgba(0,0,0,0.4)'
    ),
    dots: themed(
        'wa-dots',
        'radial-gradient(rgba(17,27,33,0.04) 1px, transparent 1px)',
        'radial-gradient(rgba(255,255,255,0.025) 1px, transparent 1px)'
    ),
}

// ── Template card tags ──────────────────────────────────────────
// The WhatsApp (Meta) status tag, the Pinpoint tag and the approval check's
// score: the STATUS pairs.
export const TAG = {
    success: STATUS.success,
    warning: STATUS.warning,
    error: STATUS.error,
    // A WhatsApp status the library does not know yet.
    neutral: STATUS.neutral,
    // Amazon Pinpoint, read-only rows.
    pinpoint: STATUS.info,
}

// ── Trainer dashboard strip preview ─────────────────────────────
// Derived, not brand values: the trainer dashboard's announcement strip
// (trainer-ui), so the notification preview looks like what trainers see.
// The same in both themes, like the dashboard. Used only by
// pages/TrainerNotifications/StripPreview.
export const TRAINER_STRIP = {
    bar: '#F0BB4F',
    text: '#FFFFFF',
    pill: '#FFFFFF',
    pillText: '#1B1B1B',
    counter: 'rgba(0,0,0,0.18)',
    ctaBorder: 'rgba(255,255,255,0.9)',
    // The tag pill's dot, per tag (trainer-ui NotificationStrip/tagStyles.ts).
    tagDot: {
        SOS: '#E5484D',
        MANDATORY: '#E5484D',
        URGENT: '#E5484D',
        IMPORTANT: '#F5A623',
        UPDATE: '#3D7BFB',
    },
    tagDotFallback: '#6B7280',
}

// ── Email content defaults (components/EmailBuilder) ────────────
// Not UI colours: the starting colours of a new email's blocks, part of the
// email the recipient sees (on an email client's white), the same in both
// themes. A template author can change each in the editor.
export const EMAIL_CONTENT = {
    text: BRAND.deepBlue,
    heading: BRAND.deepBlue,
    link: BRAND.orange,
    // The headline's highlighted phrase in an AI email (large text on white).
    highlight: BRAND.orangeDeep,
    buttonBg: BRAND.orange,
    buttonText: BRAND.deepBlue,
    divider: '#E3E5EA',
    canvas: '#FFFFFF',
    footerText: '#545A71',
    // The page colour around the email's content column (the builder's
    // "Page background" default; the value the builder always used).
    backdrop: '#F4F4F4',
    // The builder's own hints drawn ON the email (an empty image's
    // placeholder, "Empty email", a raw HTML block's code, a spacer's
    // stripes). They sit on the email's white, so they stay light-theme
    // colours in both themes: hint text 6.8:1 on white and 6.2:1 on codeBg.
    hint: '#545A71',
    hintBorder: '#CBCFD8',
    codeBg: '#F2F3F6',
    stripe: 'rgba(33,38,56,0.04)',
    // A new paragraph's and column's text size, and a new button's corner
    // radius, in the email (px; the author can change both).
    textSize: 14,
    buttonRadius: 6,
}

export const GRADIENT = {
    brand: `linear-gradient(135deg, ${BRAND.orange}, ${BRAND.yellow})`,
    // The AI actions' animated border (components/shared/GlowButton).
    glow: `linear-gradient(270deg, ${BRAND.orange}, ${BRAND.funBlue}, ${BRAND.orange})`,
}

// ── Shared sx: dropdown menu papers ─────────────────────────────
export const menuProps = {
    PaperProps: {
        sx: {
            background: SURFACE.cardRaised,
            border: `1px solid ${SURFACE.border}`,
            boxShadow: SHADOW.popover,
            '& .MuiMenuItem-root': {
                color: TEXT.secondary,
                typography: 'body2',
                '&:hover': { background: SURFACE.hover },
                '&.Mui-selected': {
                    background: ACCENT.bg15,
                    '&:hover': { background: ACCENT.bg22 },
                },
            },
        },
    },
}

// ── Shared sx: page shell ───────────────────────────────────────
// The fixed header's height (components/Navbar); the read progress bar sits
// right under it.
export const APP_BAR_HEIGHT = 48

export const pageSx = {
    margin: '-20px -24px',
    minHeight: `calc(100vh - ${APP_BAR_HEIGHT}px)`,
    background: SURFACE.page,
}

// A card: the brand's Frame (white or Deep Blue, hairline, soft shadow).
export const cardSx = {
    background: SURFACE.card,
    border: `1px solid ${SURFACE.border}`,
    borderRadius: `${RADIUS.lg}px`,
    boxShadow: SHADOW.card,
}

// A panel nested in a card (a section, a result block).
export const panelSx = {
    background: SURFACE.subtle,
    border: `1px solid ${SURFACE.border}`,
    borderRadius: `${RADIUS.md}px`,
}

/**
 * The CSS custom properties of both themes: light on `:root` (the default
 * before the theme is known), dark under `[data-theme="dark"]`. Rendered once
 * by ThemeModeProvider; switching theme only flips the attribute.
 */
export const themeVariableStyles = {
    ':root': { ...cssVariables('light'), colorScheme: 'light' },
    ':root[data-theme="dark"]': {
        ...cssVariables('dark'),
        colorScheme: 'dark',
    },
}
